/**
 * RobustScroll
 *
 * Provides a more solid scrolling experience in context of anchors.
 *
 * The browser native functionality does not reliably scroll to the correct position,
 * because of significant content shifts.
 *
 * @author David Kathmann, 03.09.2025
 */

robustScroll = {
  config: {
    smooth: true,
    focusTarget: true,
    selectorInPageLinks: 'a[href^="#"]',
    settle: {
      enabled: true,
      // Maximum time (in milliseconds) to keep trying to settle the scroll position.
      maxDurationMs: 1200,
      // How many animation frames in a row the scroll position must be stable
      // before considering the scroll "settled."
      stableFramesRequired: 3,
      // How many pixels of "wiggle room" are allowed before considering the scroll position stable.
      tolerancePx: 4
    },
    navbarOffset: {
      enabled: true,
      selector: 'div[data-sticky-navbar], div[data-menu-main] .nav-bar--secondary'
    },
    excludeAnchors: [
      /^#view/,
      '#besondereMerkmale',
      '#produktbeschreibung'
    ]
  },

  _isBound: false,
  _boundClickHandler: null,
  _boundHashChangeHandler: null,

  /**
   * Checks if a hash should be excluded from scrolling.
   * @param {string} hash - The anchor hash (e.g., "#view1").
   * @returns {boolean} True if the hash matches an exclusion.
   */
  _isExcludedAnchor(hash) {
    return this.config.excludeAnchors && this.config.excludeAnchors.some((pattern) =>
      pattern instanceof RegExp ? pattern.test(hash) : pattern === hash
    );
  },

  /**
   * Checks if the user prefers reduced motion.
   * @returns {boolean} True if the user has enabled "reduced motion" in their system settings; otherwise false.
   */
  _userPrefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  },

  /**
   * Determines if smooth scrolling should be used in general.
   * @returns {boolean} True if smooth scrolling is enabled in configuration and reduced motion is not preferred by the user.
   */
  _intentSmoothScrolling() {
    return this.config.smooth && !this._userPrefersReducedMotion();
  },

  /**
   * Gets the height of the fixed navigation bar, if present.
   * @returns {number} The height in pixels, or 0 if not found.
   */
  _getNavbarOffset() {
    if (!this.config.navbarOffset?.enabled) {
      return 0;
    }
    const navbar = document.querySelector(this.config.navbarOffset.selector);
    return navbar ? navbar.offsetHeight : 0;
  },

  /**
   * Extracts the hash fragment from an URL string.
   * @param {string} href - The URL or anchor link.
   * @returns {string} The hash part (including '#'), or an empty string if none is found.
   */
  _getHashFromHref(href) {
    try {
      const parsedUrl = new URL(href, window.location.href);
      return parsedUrl.hash || '';
    } catch {
      const hashIndex = href.indexOf('#');
      return hashIndex >= 0 ? href.slice(hashIndex) : '';
    }
  },

  /**
   * Finds the target element for a given hash.
   * @param {string} hash - The hash fragment (e.g., "#contact-form-small").
   * @returns {Element|null} The matching element, or document.body if no hash was found.
   */
  _getTargetFromHash(hash) {
    if (!hash || hash === '#') return document.body;
    const rawId = decodeURIComponent(hash.slice(1));
    const idSelector = '#' + (
      window.CSS && CSS.escape
        ? CSS.escape(rawId)
        : rawId.replace(/("|'|\\)/g, '\\$1')
    );
    return document.querySelector(idSelector) || document.querySelector(`[name="${rawId}"]`) || null;
  },

  /**
   * Computes the vertical scroll position for the target element.
   * @param {Element} targetElement - The element to scroll to.
   * @returns {number} The Y position (in pixels) from the top of the document.
   */
  _computeTargetTopY(targetElement) {
    const boundingRect = targetElement.getBoundingClientRect();
    const navbarOffset = this._getNavbarOffset();
    const targetTopY = Math.max(0, boundingRect.top + window.pageYOffset - navbarOffset);
    return Math.round(targetTopY);
  },

  /**
   * Scrolls to the target element, manages focus, and applies a soft correction for layout shifts.
   * @param {Element} targetElement - The element to scroll to.
   * @param {Object} [options={}] - Optional scroll options (e.g., { smooth: true }).
   * @returns {Promise<void>} Resolves when scrolling and settling are complete.
   */
  async _scrollToElement(targetElement, options = {}) {
    const useSmoothBehavior =
      'smooth' in options ? !!options.smooth : this._intentSmoothScrolling();

    // Initial scroll to target
    const initialTargetTopY = this._computeTargetTopY(targetElement);
    window.scrollTo({ top: initialTargetTopY, left: 0, behavior: useSmoothBehavior ? 'smooth' : 'auto' });

    // A11y-friendly focus management
    if (this.config.focusTarget && targetElement !== document.body) {
      const previousTabIndex = targetElement.getAttribute('tabindex');
      // Make the element at least temporarily focusable
      if (!targetElement.hasAttribute('tabindex')) {
        targetElement.setAttribute('tabindex', '-1');
      }
      requestAnimationFrame(() => {
        try {
          targetElement.focus({ preventScroll: true });
        } catch {
          targetElement.focus();
        }
        // Remove the tabindex again, if it was not present before
        if (previousTabIndex === null) setTimeout(() => targetElement.removeAttribute('tabindex'), 250);
      });
    }

    // Readjust for layout shifts (e.g., late-loading fonts, images and so on)
    if (!this.config.settle?.enabled) return;

    const { tolerancePx, stableFramesRequired, maxDurationMs } = this.config.settle;
    const deadline = performance.now() + maxDurationMs;

    let consecutiveStableFrames = 0;
    let lastTargetTopY = initialTargetTopY;

    await new Promise((resolve) => {
      const verifyAndAdjust = () => {
        const now = performance.now();
        const targetTopY = this._computeTargetTopY(targetElement);
        // Distance between current scroll position and target position
        const delta = Math.abs(window.pageYOffset - targetTopY);

        // When the scroll position is within the tolerance, count it as a stable frame and
        // if enough stable frames have been reached, finish.
        if (delta <= tolerancePx) {
          consecutiveStableFrames += 1;
          if (consecutiveStableFrames >= stableFramesRequired) {
            return resolve();
          }
        } else {
          consecutiveStableFrames = 0;
          // Only scroll again if the target position has actually changed
          if (targetTopY !== lastTargetTopY) {
            // Instantly adjust scroll position (no smooth) to avoid "fighting" with layout shifts
            window.scrollTo({ top: targetTopY, left: 0, behavior: 'auto' });
            lastTargetTopY = targetTopY;
          }
        }

        if (now >= deadline) return resolve();
        requestAnimationFrame(verifyAndAdjust);
      };

      // Wait two frames to let initial layout work (e.g., fonts) finish
      requestAnimationFrame(() => requestAnimationFrame(verifyAndAdjust));
    });

  },

  /**
   * Handles in-page anchor navigation: updates browser history and scrolls to the target element.
   * @param {string} hash - The hash fragment to navigate to (e.g., "#contact-form-small").
   * @param {Object} [options={}] - Navigation options.
   * @param {boolean} [options.push=true] - If true, use history.pushState to add a new entry.
   * @param {boolean} [options.replace=false] - If true, use history.replaceState to replace the current entry.
   * @returns {void}
   */
  _handleInPageNavigation(hash, { push = true, replace = false } = {}) {
    const targetElement = this._getTargetFromHash(hash);
    if (!targetElement) {
      return;
    }

    if (replace) {
      history.replaceState(history.state, '', hash || window.location.pathname + window.location.search);
    } else if (push) {
      history.pushState(history.state, '', hash || window.location.pathname + window.location.search);
    }

    this._scrollToElement(targetElement);
  },

  /**
   * Handles click events on in-page anchor links.
   * Intercepts anchor clicks, checks if the link is an in-page anchor on the current page,
   * prevents the default browser behavior, and triggers custom anchor navigation and scrolling.
   * @param {MouseEvent} domEvent - The click event object.
   * @returns {void}
   */
  _onClick(domEvent) {

    const anchorElement = domEvent.target.closest(this.config.selectorInPageLinks);
    if (!anchorElement) return;

    const hash = this._getHashFromHref(anchorElement.getAttribute('href') || '');
    if (!hash && anchorElement.getAttribute('href') !== '#') {
      return;
    }
    if (this._isExcludedAnchor(hash)) return;

    try {
      const linkUrl = new URL(anchorElement.href);
      const currentUrl = new URL(window.location.href);
      if (
        linkUrl.origin !== currentUrl.origin
        || linkUrl.pathname !== currentUrl.pathname
        || linkUrl.search !== currentUrl.search
      ) {
        return;
      }
    } catch {}

    domEvent.preventDefault();

    if (!hash || hash === '#' || hash.toLowerCase() === '#top') {
      this._handleInPageNavigation('', { push: true });
      this._scrollToElement(document.body);
      return;
    }

    this._handleInPageNavigation(hash, { push: true });
  },

  /**
   * Handles hashchange events in the browser.
   * Prevents the default browser behavior, extracts the current hash from the URL,
   * and triggers custom in-page navigation and scrolling logic.
   * @param {HashChangeEvent} hashChangeEvent - The hashchange event object.
   * @returns {void}
   */
  _onHashChange(hashChangeEvent) {
    if (hashChangeEvent && hashChangeEvent.preventDefault) hashChangeEvent.preventDefault();
    const { hash: currentHash } = window.location;
    if (this._isExcludedAnchor(currentHash)) return;
    this._handleInPageNavigation(currentHash, { push: false, replace: true });
  },

  /**
   * Handles the initial hash in the URL on page load.
   * If a hash is present, triggers custom in-page navigation and scrolls to the target element.
   * Also applies a correction after all resources are loaded to account for late layout shifts (e.g., images, fonts).
   * @returns {void}
   */
  _onInitialHash() {
    const performInitialScroll = () => {
      const { hash: initialHash } = window.location;
      if (!initialHash) {
        return;
      }
      this._handleInPageNavigation(initialHash, { push: false, replace: true });

      // Safety net: correct again after everything has fully loaded
      const finalCorrection = () => {
        const targetElement = this._getTargetFromHash(window.location.hash);
        if (targetElement) {
          this._scrollToElement(targetElement, { smooth: false });
        }
      };

      window.addEventListener('load', () => {
        // Immediately after load and once again shortly after (often: late image decodes)
        finalCorrection();
        setTimeout(finalCorrection, 150);
      }, { once: true });
    };

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      requestAnimationFrame(() => requestAnimationFrame(performInitialScroll));
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        requestAnimationFrame(() => requestAnimationFrame(performInitialScroll));
      }, { once: true });
    }
  },

  /**
   * Initializes the anchor jumper: sets up config, event listeners, and scroll restoration.
   * @param {Object} [options={}] - Optional configuration overrides.
   * @returns {void}
   */
  init(options = {}) {
    if (this._isBound) {
      return;
    }
    this._isBound = true;

    // Merge of default config with developer-provided options
    this.config = Object.assign({}, this.config, options || {});
    // Prevent default scroll restoration behavior of the browser if supported
    try {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    } catch {
      // Do Nothing :)
    }

    this._boundClickHandler = this._onClick.bind(this);
    this._boundHashChangeHandler = this._onHashChange.bind(this);
    document.addEventListener('click', this._boundClickHandler, true);
    window.addEventListener('hashchange', this._boundHashChangeHandler);

    this._onInitialHash();
  },

  /**
   * Removes all event listeners and restores scroll behavior.
   * @returns {void}
   */
  destroy() {
    if (!this._isBound) {
      return;
    }
    this._isBound = false;

    document.removeEventListener('click', this._boundClickHandler, true);
    window.removeEventListener('hashchange', this._boundHashChangeHandler);
    try {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    } catch {
      // Do nothing :)
    }

    this._boundClickHandler = null;
    this._boundHashChangeHandler = null;
  }
};
